import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, Info } from "lucide-react";
import { api } from "@/lib/api";

const TAB_LABEL = { budgets: "Budgets", weights: "What matters to us", calibration: "Calibration", location: "Where we are" };

/** Flag at the top of the Radar: the value model of this set is unreviewed, unweighted or uncalibrated (+ clients in the same state, admins). */
export function ValueWarningsBanner() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(true);
    useEffect(() => {
        Promise.allSettled([api.get("/v1/ripeness/value/assumptions"), api.get("/v1/ripeness/value/health")]).then(([s, h]) => {
            setData({ warnings: s.status === "fulfilled" ? s.value.data.warnings || [] : [], health: h.status === "fulfilled" ? h.value.data : null });
        });
    }, []);
    if (!data) return null;
    const clientsManual = data.health?.clients?.filter((c) => c.status === "manual").length || 0;
    const clientsNoWeights = data.health?.clients?.filter((c) => !c.has_weights).length || 0;
    const items = [...data.warnings];
    if (data.health && (clientsManual || clientsNoWeights)) {
        items.push({ code: "clients", severity: "info", title: "Clients with unreviewed value models",
            message: `${clientsNoWeights} client set${clientsNoWeights === 1 ? "" : "s"} without weights · ${clientsManual} without any CTA data (manual values) — every untouched client inherits the flags above.` });
    }
    if (!items.length) return null;
    const warn = items.some((w) => w.severity === "warning");
    const tone = warn ? "border-amber-400/50 bg-amber-400/10 text-amber-100" : "border-sky-400/40 bg-sky-400/10 text-sky-100";
    const Icon = warn ? AlertTriangle : Info;
    return (
        <div className={`rounded-lg border px-4 py-3 mb-4 ${tone}`} role="alert" data-testid="value-warnings-banner">
            <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left" data-testid="value-warnings-toggle">
                <Icon className="w-5 h-5 shrink-0" />
                <span className="font-semibold text-sm flex-1" data-testid="value-warnings-title">
                    Lead values need attention — {(items.filter((w) => w.severity === "warning").length ? items.filter((w) => w.severity === "warning") : items).map((w) => w.title).join(" · ")}
                </span>
                <Link to="/qa/value-assumptions" onClick={(e) => e.stopPropagation()} className="text-xs underline underline-offset-2 whitespace-nowrap" data-testid="value-warnings-link">Fix in Value assumptions →</Link>
                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            {open ? (
                <ul className="mt-2 space-y-1 text-xs" data-testid="value-warnings-list">
                    {items.map((w) => (
                        <li key={w.code} className="flex items-start gap-2" data-testid={`value-warning-${w.code}`}>
                            <span className={`mt-1 inline-block w-1.5 h-1.5 rounded-full shrink-0 ${w.severity === "warning" ? "bg-amber-300" : "bg-sky-300"}`} />
                            <span className="opacity-90"><b>{w.title}.</b> {w.message}{w.tab ? <> <Link to="/qa/value-assumptions" className="underline underline-offset-2 opacity-80">{TAB_LABEL[w.tab] || "Open"} tab</Link></> : null}</span>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}
